'use client'

import React, { useRef, useEffect, useState } from 'react'

interface SWSFallbackVisualizerProps {
  audioData?: Float32Array
  neuralActivity?: number[]
  frequencyData?: Float32Array
  width: number
  height: number
  mode?: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial'
  className?: string
}

class CanvasVisualizationEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private animationFrameId: number | null = null
  private time = 0
  private dataPoints: Float32Array = new Float32Array(1024)
  private neuralWeights: Float32Array = new Float32Array(256)
  private particles: Array<{ x: number; y: number; vx: number; vy: number; life: number }> = []

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
    this.initializeParticles()
  }

  private initializeParticles(): void {
    this.particles = []
    for (let i = 0; i < 150; i++) {
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4,
        life: Math.random()
      })
    }
  }

  updateData(audioData?: Float32Array, neuralActivity?: number[], frequencyData?: Float32Array): void {
    if (audioData) {
      this.dataPoints.set(audioData.slice(0, 1024))
    }
    if (neuralActivity) {
      this.neuralWeights.set(neuralActivity.slice(0, 256).map(v => v * 2 - 1))
    }
  }

  render(mode: string): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    switch (mode) {
      case 'waveform':
        this.renderWaveform()
        break
      case 'spectrum':
        this.renderSpectrum()
        break
      case 'neural':
        this.renderNeuralNetwork()
        break
      case 'particles':
        this.renderParticles()
        break
      case 'spatial':
        this.renderSpatial3D()
        break
      default:
        this.renderWaveform()
    }

    this.time += 0.016
  }

  private renderWaveform(): void {
    const { width, height } = this.canvas
    const centerY = height / 2

    this.ctx.strokeStyle = '#00ffff'
    this.ctx.lineWidth = 2
    this.ctx.beginPath()

    const step = Math.max(1, Math.floor(this.dataPoints.length / width))

    for (let i = 0; i < width; i++) {
      const dataIndex = Math.floor((i / width) * this.dataPoints.length)
      const sample = this.dataPoints[dataIndex] || 0
      const y = centerY + sample * centerY * 0.8

      if (i === 0) {
        this.ctx.moveTo(i, y)
      } else {
        this.ctx.lineTo(i, y)
      }
    }

    this.ctx.stroke()

    // Add glow effect
    this.ctx.shadowColor = '#00ffff'
    this.ctx.shadowBlur = 10
    this.ctx.stroke()
    this.ctx.shadowBlur = 0
  }

  private renderSpectrum(): void {
    const { width, height } = this.canvas
    const barWidth = width / 256
    const maxHeight = height * 0.8

    for (let i = 0; i < 256; i++) {
      const dataIndex = Math.floor((i / 256) * this.dataPoints.length)
      const magnitude = Math.abs(this.dataPoints[dataIndex] || 0)
      const barHeight = magnitude * maxHeight

      // Color based on frequency
      const hue = (i / 256) * 360
      this.ctx.fillStyle = `hsl(${hue}, 70%, 50%)`

      this.ctx.fillRect(
        i * barWidth,
        height - barHeight,
        barWidth - 1,
        barHeight
      )
    }

    // Add glow effect
    this.ctx.shadowColor = '#ffffff'
    this.ctx.shadowBlur = 5
    for (let i = 0; i < 256; i++) {
      const dataIndex = Math.floor((i / 256) * this.dataPoints.length)
      const magnitude = Math.abs(this.dataPoints[dataIndex] || 0)
      const barHeight = magnitude * maxHeight

      if (magnitude > 0.3) {
        this.ctx.fillRect(
          i * barWidth,
          height - barHeight,
          barWidth - 1,
          barHeight
        )
      }
    }
    this.ctx.shadowBlur = 0
  }

  private renderNeuralNetwork(): void {
    const { width, height } = this.canvas
    const nodeRadius = 3
    const layerSpacing = width / 6
    const nodeSpacing = height / 16

    // Draw connections
    this.ctx.strokeStyle = 'rgba(0, 255, 255, 0.2)'
    this.ctx.lineWidth = 1

    for (let layer = 0; layer < 5; layer++) {
      for (let node = 0; node < 16; node++) {
        const x1 = (layer + 1) * layerSpacing
        const y1 = (node + 1) * nodeSpacing

        if (layer < 4) {
          for (let nextNode = 0; nextNode < 16; nextNode++) {
            const x2 = (layer + 2) * layerSpacing
            const y2 = (nextNode + 1) * nodeSpacing

            const weightIndex = (layer * 16 + node) % this.neuralWeights.length
            const weight = this.neuralWeights[weightIndex] || 0
            const alpha = Math.abs(weight) * 0.5

            this.ctx.strokeStyle = `rgba(0, 255, 255, ${alpha})`
            this.ctx.beginPath()
            this.ctx.moveTo(x1, y1)
            this.ctx.lineTo(x2, y2)
            this.ctx.stroke()
          }
        }
      }
    }

    // Draw nodes
    for (let layer = 0; layer < 6; layer++) {
      for (let node = 0; node < 16; node++) {
        const x = (layer + 1) * layerSpacing
        const y = (node + 1) * nodeSpacing

        const weightIndex = (layer * 16 + node) % this.neuralWeights.length
        const activation = Math.abs(this.neuralWeights[weightIndex] || 0)

        // Node color based on activation
        const intensity = activation * 255
        this.ctx.fillStyle = `rgb(${intensity}, ${255 - intensity}, 255)`

        this.ctx.beginPath()
        this.ctx.arc(x, y, nodeRadius, 0, Math.PI * 2)
        this.ctx.fill()

        // Glow effect for active nodes
        if (activation > 0.5) {
          this.ctx.shadowColor = '#00ffff'
          this.ctx.shadowBlur = 10
          this.ctx.fill()
          this.ctx.shadowBlur = 0
        }
      }
    }
  }

  private renderParticles(): void {
    const { width, height } = this.canvas

    // Update particles
    this.particles.forEach(particle => {
      particle.x += particle.vx
      particle.y += particle.vy
      particle.life -= 0.01

      // Bounce off walls
      if (particle.x < 0 || particle.x > width) particle.vx *= -1
      if (particle.y < 0 || particle.y > height) particle.vy *= -1

      // Respawn dead particles
      if (particle.life <= 0) {
        particle.x = Math.random() * width
        particle.y = Math.random() * height
        particle.vx = (Math.random() - 0.5) * 4
        particle.vy = (Math.random() - 0.5) * 4
        particle.life = 1
      }
    })

    // Draw particles
    this.particles.forEach((particle, index) => {
      const dataIndex = Math.floor((index / this.particles.length) * this.dataPoints.length)
      const intensity = Math.abs(this.dataPoints[dataIndex] || 0)

      const size = (intensity * 8 + 2) * particle.life
      const alpha = intensity * 0.8 + 0.2

      // Color based on intensity
      const hue = (intensity * 240 + 120) % 360
      this.ctx.fillStyle = `hsla(${hue}, 70%, 60%, ${alpha})`

      this.ctx.beginPath()
      this.ctx.arc(particle.x, particle.y, size, 0, Math.PI * 2)
      this.ctx.fill()

      // Add glow
      this.ctx.shadowColor = `hsl(${hue}, 70%, 60%)`
      this.ctx.shadowBlur = size * 2
      this.ctx.fill()
      this.ctx.shadowBlur = 0
    })
  }

  private renderSpatial3D(): void {
    const { width, height } = this.canvas
    const gridSize = 32
    const cellWidth = width / gridSize
    const cellHeight = height / gridSize

    for (let x = 0; x < gridSize; x++) {
      for (let y = 0; y < gridSize; y++) {
        const dataIndex = Math.floor(((x * gridSize + y) / (gridSize * gridSize)) * this.dataPoints.length)
        const intensity = Math.abs(this.dataPoints[dataIndex] || 0)

        // 3D-like height calculation
        const height3D = Math.sin(x * 0.3 + this.time) * Math.cos(y * 0.3 + this.time) * intensity

        // Color based on height
        const hue = (height3D + 1) * 120
        const lightness = 30 + intensity * 40

        this.ctx.fillStyle = `hsl(${hue}, 70%, ${lightness}%)`
        this.ctx.fillRect(
          x * cellWidth,
          y * cellHeight,
          cellWidth - 1,
          cellHeight - 1
        )
      }
    }

    // Add wireframe effect
    this.ctx.strokeStyle = 'rgba(0, 255, 255, 0.3)'
    this.ctx.lineWidth = 1

    for (let i = 0; i <= gridSize; i++) {
      // Vertical lines
      this.ctx.beginPath()
      this.ctx.moveTo(i * cellWidth, 0)
      this.ctx.lineTo(i * cellWidth, height)
      this.ctx.stroke()

      // Horizontal lines
      this.ctx.beginPath()
      this.ctx.moveTo(0, i * cellHeight)
      this.ctx.lineTo(width, i * cellHeight)
      this.ctx.stroke()
    }
  }

  startRendering(mode: string): void {
    const renderLoop = () => {
      this.render(mode)
      this.animationFrameId = requestAnimationFrame(renderLoop)
    }
    renderLoop()
  }

  stopRendering(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  dispose(): void {
    this.stopRendering()
  }
}

const SWSFallbackVisualizer: React.FC<SWSFallbackVisualizerProps> = ({
  audioData,
  neuralActivity,
  frequencyData,
  width,
  height,
  mode = 'waveform',
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<CanvasVisualizationEngine | null>(null)
  const [currentMode, setCurrentMode] = useState(mode)

  useEffect(() => {
    if (canvasRef.current && !engineRef.current) {
      engineRef.current = new CanvasVisualizationEngine(canvasRef.current)
      engineRef.current.startRendering(currentMode)
    }

    return () => {
      if (engineRef.current) {
        engineRef.current.dispose()
      }
    }
  }, [currentMode])

  useEffect(() => {
    if (engineRef.current) {
      engineRef.current.updateData(audioData, neuralActivity, frequencyData)
    }
  }, [audioData, neuralActivity, frequencyData])

  useEffect(() => {
    if (engineRef.current && currentMode !== mode) {
      setCurrentMode(mode)
      engineRef.current.stopRendering()
      engineRef.current.startRendering(mode)
    }
  }, [mode, currentMode])

  const modes = [
    { id: 'waveform', name: 'Waveform', icon: '📊' },
    { id: 'spectrum', name: 'Spectrum', icon: '🎵' },
    { id: 'neural', name: 'Neural Net', icon: '🧠' },
    { id: 'particles', name: 'Particles', icon: '✨' },
    { id: 'spatial', name: '3D Spatial', icon: '🌌' }
  ]

  return (
    <div className={`sws-fallback-visualizer ${className}`}>
      <div className="bg-black/90 backdrop-blur-md rounded-xl p-4 border border-purple-500/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-purple-400">Canvas Visualizer</h3>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-xs text-purple-300/60">Canvas2D Active</span>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="flex flex-wrap gap-2 mb-4">
          {modes.map((modeOption) => (
            <button
              key={modeOption.id}
              onClick={() => setCurrentMode(modeOption.id as typeof currentMode)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                currentMode === modeOption.id
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                  : 'bg-purple-900/30 text-purple-300 hover:bg-purple-800/50'
              }`}
            >
              {modeOption.icon} {modeOption.name}
            </button>
          ))}
        </div>

        {/* Canvas */}
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="w-full border border-purple-500/20 rounded-lg bg-black"
        />

        {/* Performance Info */}
        <div className="mt-4 text-xs text-purple-400/40 text-center">
          <p>🎨 Canvas2D Rendering • Real-time Visualization • Fallback Mode</p>
        </div>
      </div>
    </div>
  )
}

export default SWSFallbackVisualizer